import { Box, Typography } from '@mui/material'
import T from 'prop-types'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { isRepairSession } from 'lib/sessionLifecycle'

import ConfirmBar from 'components/atoms/ConfirmBar'
import Table from 'components/atoms/Table'
import TableHeaderCell from 'components/atoms/TableHeaderCell'
import TitleBar from 'components/atoms/TitleBar'
import {
  resolveBlindNegativeCap,
  resolveBlindPositiveCap,
  resolveCapFirstChoice,
} from 'components/organisms/Sessions/sessionsLib'

import CapField from './CapField'

const createColumns = (t, items) => [
  {
    accessor: 'partnerGroup',
    Header: t('sessions.caps.table.partnerGroup'),
  },
  {
    accessor: 'partner',
    Header: t('sessions.caps.table.partner'),
  },
  {
    accessor: 'capFirstChoice',
    Header: (
      <TableHeaderCell
        label={t('sessions.caps.table.firstChoice')}
        errorsNumber={
          items.filter((item) => item.hasCap && item.capFirstChoiceMissing)
            .length
        }
      />
    ),
  },
  {
    accessor: 'blindFirstChoice',
    Header: (
      <TableHeaderCell
        label={t('sessions.blindCaps.table.firstChoice')}
        errorsNumber={
          items.filter(
            (item) => item.hasBlindCap && item.blindFirstChoiceMissing
          ).length
        }
      />
    ),
  },
  {
    accessor: 'blindSecondChoice',
    Header: (
      <TableHeaderCell
        label={t('sessions.blindCaps.table.secondChoice')}
        errorsNumber={
          items.filter(
            (item) => item.hasBlindCap && item.blindSecondChoiceMissing
          ).length
        }
      />
    ),
  },
]

export default function SessionsCaps({
  session,
  updateCap,
  updateBlindCap,
  handleValidation,
  handleConfirm,
  confirmed,
  readOnly,
  contentIsReadOnly = false,
}) {
  const { t } = useTranslation()

  const caps = useMemo(() => session.caps || [], [session.caps])
  const blindCaps = useMemo(() => session.blindCaps || [], [session.blindCaps])
  const repairSession = isRepairSession(session)

  const combinedItems = useMemo(() => {
    const capsById = caps.reduce((result, item) => {
      result[item.partnerId] = item
      return result
    }, {})
    const blindCapsById = blindCaps.reduce((result, item) => {
      result[item.partnerId] = item
      return result
    }, {})

    const partnerIds = Array.from(
      new Set([
        ...caps.map((item) => item.partnerId),
        ...blindCaps.map((item) => item.partnerId),
      ])
    )

    return partnerIds.map((partnerId) => {
      const cap = capsById[partnerId]
      const blindCap = blindCapsById[partnerId]
      return {
        partnerId,
        partnerGroup: cap?.partnerGroup || blindCap?.partnerGroup,
        partner: cap?.name || blindCap?.name,
        capFirstChoice: resolveCapFirstChoice(session, cap),
        blindFirstChoice: resolveBlindPositiveCap(session, blindCap),
        blindSecondChoice: resolveBlindNegativeCap(blindCap),
        capFirstChoiceMissing: repairSession ? false : !cap?.firstChoice,
        blindFirstChoiceMissing: repairSession ? false : !blindCap?.firstChoice,
        blindSecondChoiceMissing:
          blindCap?.blindNegativeCap == null && blindCap?.secondChoice == null,
        hasCap: !!cap,
        hasBlindCap: !!blindCap,
      }
    })
  }, [blindCaps, caps, repairSession, session])

  const columns = useMemo(
    () => createColumns(t, combinedItems),
    [combinedItems, t]
  )

  const handleCapChange = (event) => {
    const [scope, partnerId, type] = event.target.id.split('.')
    const value = Number(event.target.value)

    if (scope === 'blind') {
      updateBlindCap(session.sessionId, partnerId, type, value)
      return
    }

    updateCap(session.sessionId, partnerId, type, value)
  }

  const isCapsReadOnly = readOnly || confirmed || contentIsReadOnly
  const blindPositiveIsReadOnly = isCapsReadOnly || repairSession

  const preparedItems = combinedItems.map((item) => ({
    partnerGroup: item.partnerGroup,
    partner: item.partner,
    blindFirstChoice: item.hasBlindCap ? (
      <CapField
        id={`blind.${item.partnerId}.first`}
        value={item.blindFirstChoice}
        readOnly={blindPositiveIsReadOnly}
        handleCapChange={handleCapChange}
        canBeEmpty
        disabled={blindPositiveIsReadOnly}
      />
    ) : (
      '-'
    ),
    blindSecondChoice: item.hasBlindCap ? (
      <CapField
        id={`blind.${item.partnerId}.second`}
        value={item.blindSecondChoice}
        readOnly={isCapsReadOnly}
        handleCapChange={handleCapChange}
        disabled={isCapsReadOnly}
      />
    ) : (
      '-'
    ),
    capFirstChoice: item.hasCap ? (
      <CapField
        id={`cap.${item.partnerId}.first`}
        value={item.capFirstChoice}
        readOnly={isCapsReadOnly}
        handleCapChange={handleCapChange}
        canBeEmpty
        disabled={isCapsReadOnly}
      />
    ) : (
      '-'
    ),
  }))

  const totalMissing = useMemo(() => {
    if (repairSession) {
      return 0
    }

    return (
      caps.reduce(
        (result, current) => result + Number(!current.firstChoice),
        0
      ) +
      blindCaps.reduce(
        (result, current) => result + Number(!current.firstChoice),
        0
      )
    )
  }, [blindCaps, caps, repairSession])

  return (
    <div data-testid="session-caps">
      <Box mt={1} />
      <ConfirmBar
        label={t(`sessions.caps.confirmButton`)}
        right={
          totalMissing ? (
            <>
              <Typography
                component="span"
                variant="body2"
                color="textSecondary"
                sx={{ paddingRight: (theme) => theme.spacing(0.5) }}
              >
                <strong>{t('sessions.caps.capsMissing')}</strong>
              </Typography>
              <Typography component="span" variant="body2" color="error">
                <strong>{totalMissing}</strong>
              </Typography>
            </>
          ) : null
        }
        disabled={!handleValidation() || isCapsReadOnly}
        handleConfirm={handleConfirm}
      />
      <TitleBar
        title={t(
          repairSession
            ? 'sessions.caps.repair.contentTitle'
            : 'sessions.caps.contentTitle'
        )}
        description={t(
          repairSession
            ? 'sessions.caps.repair.contentDescription'
            : 'sessions.caps.contentDescription'
        )}
      />
      <Table items={preparedItems} columns={columns} id="caps-list" />
    </div>
  )
}

SessionsCaps.propTypes = {
  session: T.object.isRequired,
  updateCap: T.func.isRequired,
  updateBlindCap: T.func.isRequired,
  handleValidation: T.func.isRequired,
  handleConfirm: T.func.isRequired,
  confirmed: T.bool,
  readOnly: T.bool,
  contentIsReadOnly: T.bool,
}
